import React, { useEffect, useMemo, useRef, useState } from 'react';

import { MOUSE_DOWN_EVENT } from 'constants/events';
import { debounce, isEmpty, map, min } from 'lodash';
import { useStandardsList } from 'sdk';

import { ReactComponent as SearchIcon } from 'pages/Teachers/Assignments/CreateMathV2/assets/search-icon.svg';
import { ReactComponent as StandardIcon } from 'pages/Teachers/Assignments/CreateMathV2/assets/standard-icon.svg';
import { getOptionLabel } from 'utils/assignments';
import { buildFilters } from 'utils/filters';

import styles from './styles.module.scss';

const StandardSelector = ({
  selectedStandards = [],
  setSelectedStandards,
  showLabel = true,
  subject,
  placeholder = 'Search and add standards...',
  value = '',
  onChange,
  error
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef();

  const handleSelectStandard = ({ standard }) => {
    if (
      setSelectedStandards &&
      !selectedStandards.find((s) => s.id === standard.id)
    ) {
      setSelectedStandards((prev) => [...prev, standard]);
    }
    setSearchTerm('');
    setIsOpen(false);
  };

  const debouncedSetStandardSearchValue = debounce(
    (event) => setSearchParams(event.target.value),
    300
  );

  const params = useMemo(
    () =>
      buildFilters({
        search: searchParams,
        'exclude_standards[]': [...map(selectedStandards, 'id')],
        math_standards: subject === 'math' ? true : false,
        retrieve_root_standards: false
      }),
    [searchParams, selectedStandards, subject]
  );

  const removeStandard = (id) => {
    if (setSelectedStandards) {
      setSelectedStandards((prev) => prev.filter((s) => s.id !== id));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
    return () =>
      document.removeEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
  }, [value, isOpen]);

  const { data, loading: isLoading } = useStandardsList({ params });
  const minLevel = useMemo(() => min(map(data?.results, 'level')) || 0, [data]);

  return (
    <div className={styles.fieldSection} ref={containerRef}>
      {showLabel && (
        <div className={styles.fieldLabel}>
          <StandardIcon /> <span>Learning Standards</span>
        </div>
      )}
      <div className={styles.searchContainer}>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            className={styles.inputField}
            placeholder={placeholder}
            value={searchTerm}
            onFocus={() => setIsOpen(true)}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              debouncedSetStandardSearchValue(e);
              if (onChange) {
                onChange('');
              }
            }}
          />
          <SearchIcon className={styles.searchIcon} />
        </div>

        {isOpen && !isEmpty(searchTerm) && (
          <div className={styles.dropdown}>
            {isLoading ? (
              <div className={styles.message}>Loading...</div>
            ) : !isEmpty(data?.results) ? (
              map(data?.results, (standard) => {
                const paddingLeft = (standard.level - minLevel) * 16 + 12;
                return (
                  <div
                    key={standard.id}
                    className={styles.option}
                    style={{ paddingLeft: `${paddingLeft}px` }}
                    onClick={() => handleSelectStandard({ standard: standard })}
                  >
                    <span>{getOptionLabel({ option: standard })}</span>
                  </div>
                );
              })
            ) : (
              <div className={styles.message}>No matches found</div>
            )}
          </div>
        )}
      </div>

      {!isEmpty(selectedStandards) && (
        <div className={styles.chipsContainer}>
          {selectedStandards.map((standard) => (
            <div key={standard.id} className={styles.standardChip}>
              <span className={styles.chipText}>{standard.code}</span>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeStandard(standard.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

export default StandardSelector;
